import * as React from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";

// UI
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { getQuestion } from "@/services/board-of-directors";
import { toast } from "sonner";
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
} from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { postAnswer } from "@/services/board-of-directors";

import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import TableSkeleton from "@/components/skeleton/TableSkeleton";

// Services and Types
interface Question {
	id: string;
	index: number;
	question: string;
}

interface Board {
	id: string;
	index: number;
	title: string;
	boardDirectorDetails: Question[];
}

interface FormState {
	[key: string]: {
		answer: number | string;
		description: string;
	};
}

export const Route = createFileRoute(
	"/_layout-private/report/board-of-directors/details/answer/",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate();
	const ID = String(localStorage.getItem("Question"));
	const companyId = localStorage.getItem("companyId"); // Add this line
	const [formData, setFormData] = useState<FormState>({});
	const [errors, setErrors] = useState<{ [key: string]: string }>({});
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { data: boardsData, isLoading } = useQuery({
		queryKey: ["boards", ID],
		queryFn: () => getQuestion(ID),
	});

	const The_data = boardsData?.data?.boardDirectorTitles;

	// Save formId to localStorage
	React.useEffect(() => {
		if (The_data?.[0]?.formId) {
			localStorage.setItem("formId", The_data[0].formId);
		}
	}, [The_data]);

	const validateInput = (value: number, questionId: string) => {
		if (!value) {
			setErrors((prev) => ({ ...prev, [questionId]: "ກະລຸນາໃສ່ຄະແນນ" }));
			return false;
		}
		if (value < 1 || value > 10) {
			setErrors((prev) => ({
				...prev,
				[questionId]: "ຄະແນນຕ້ອງຢູ່ລະຫວ່າງ 1-10",
			}));
			return false;
		}
		setErrors((prev) => ({ ...prev, [questionId]: "" }));
		return true;
	};

	const handleInputChange = (
		questionId: string,
		value: string,
		field: "answer" | "description",
	) => {
		setFormData((prev) => ({
			...prev,
			[questionId]: {
				...prev[questionId],
				[field]: value,
			},
		}));
		if (field === "answer") {
			validateInput(Number(value), questionId);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);

		if (!companyId) {
			toast.error("No company ID found");
			return;
		}

		try {
			// Validate all answers
			const hasErrors = The_data?.some((board) =>
				board.boardDirectorDetails.some((detail) => {
					const answer = Number(formData[detail.id]?.answer);
					return !answer || answer < 1 || answer > 10;
				}),
			);

			if (hasErrors) {
				toast.error("ກະລຸນາກວດສອບຄ່າຄະແນນໃຫ້ຖືກຕ້ອງ");
				return;
			}

			const payload = {
				fromId: localStorage.getItem("formId") || "",
				isDirectorForm: false,
				boardId: localStorage.getItem("boardId") || "",
				companyId: companyId, // Add this line
				boarDirectorAnswerTitles: The_data?.map((board) => ({
					titleAnswer: board.title,
					boarDirectorAnswerDetails: board.boardDirectorDetails.map(
						(detail) => ({
							question: detail.question,
							questionId: detail.id,
							answer: Number(formData[detail.id]?.answer || 0),
							description: formData[detail.id]?.description || null,
						}),
					),
				})),
			};

			await postAnswer(payload);
			toast.success("ສົ່ງແບບຟອມສຳເລັດແລ້ວ");
			navigate({ to: "/report/board-of-directors/details" });
		} catch (error) {
			toast.error("ເກີດຂໍ້ຜິດພາດ");
			console.error("Error submitting answers:", error);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (isLoading) return <TableSkeleton />;
	if (!The_data) return <div>No data available</div>;

	return (
		<section>
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbLink>
							<Link
								replace
								to="/report/board-of-directors/details?active=Government"
							>
								ການປະເມີນ
							</Link>
						</BreadcrumbLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbPage>ຕອບແບບຟອມການປະເມີນ</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>

			<Card className="mt-8">
				<CardHeader>
					<button
						type="button"
						onClick={() => {
							try {
								navigate({
									to: "/report/board-of-directors/details",
									replace: true,
								});
							} catch (error) {
								console.error("Navigation error:", error);
							}
						}}
						className="flex gap-1 items-center mb-4"
						aria-label="Go back to tax obligations report"
					>
						<ArrowLeft className="w-5 h-5" />
						ກັບຄືນ
					</button>
					<CardTitle>ການປະເມີນຄະນະອຳນວຍການ</CardTitle>
					<CardDescription>ລາຍການການປະເມີນ</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit}>
						<Table className="my-4 w-full">
							<TableHeader>
								<TableRow className="[&>*]:font-bold [&>*]:text-black  [&>*]:whitespace-nowrap">
									<TableHead className="w-1/12 border">ລາດັບ </TableHead>
									<TableHead className="border">ຄຳຖາມການປະເມີນ</TableHead>
									<TableHead className="border"> ຕອບຄຳຖາມ </TableHead>
									<TableHead className="w-2/12 border">
										ຫຼັກການໃຫ້ຄະແນນ (1-10)
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{The_data?.map((board: Board) => (
									<React.Fragment key={board.id}>
										<TableRow className="[&>*]:font-bold [&>*]:bg-gray-100">
											<TableCell className="font-medium border">
												{board.index}
											</TableCell>
											<TableCell className="font-medium border" colSpan={3}>
												{board.title}
											</TableCell>
										</TableRow>
										{board.boardDirectorDetails.map((question) => (
											<TableRow key={question.id} className="cursor-pointer">
												<TableCell className="font-medium border">
													{board.index}.{question.index}
												</TableCell>
												<TableCell className="font-medium border">
													{question.question}
												</TableCell>
												<TableCell className="font-medium border">
													<Input
														type="text"
														placeholder="ຕອບຄຳຖາມ..."
														value={formData[question.id]?.description || ""}
														onChange={(e) =>
															handleInputChange(
																question.id,
																e.target.value,
																"description",
															)
														}
													/>
												</TableCell>
												<TableCell className="font-medium border">
													<div>
														<Input
															type="number"
															min={1}
															max={10}
															placeholder="ໃຫ້ຄະແນນ..."
															value={formData[question.id]?.answer || ""}
															onChange={(e) =>
																handleInputChange(
																	question.id,
																	e.target.value,
																	"answer",
																)
															}
														/>
														{errors[question.id] && (
															<div className="text-sm text-red-500">
																{errors[question.id]}
															</div>
														)}
													</div>
												</TableCell>
											</TableRow>
										))}
									</React.Fragment>
								))}
							</TableBody>
						</Table>

						<div className="flex gap-x-6 justify-end mb-4 w-full">
							<Button
								type="button"
								variant="outline"
								onClick={() =>
									navigate({ to: "/report/board-of-directors/details" })
								}
							>
								ຍົກເລີກ
							</Button>
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting ? "ກຳລັງສົ່ງ..." : "ສົ່ງແບບຟອມ"}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</section>
	);
}
